import React, { useRef, useState, useEffect } from "react";
import { UploadingFile } from "../../types/api/file";
import { useUploadFileMutation } from "../../hooks/useFileMutation";
import { Stack, Button } from "@primer/react";
import {
  PlusIcon,
} from "@primer/octicons-react";
import { useAlert } from "../../context/alert";

interface FileUploadComponentProps {
  isDisabledUploadingButton: boolean
  onFileUploaded: (file: UploadingFile | undefined) => void;
  onUploadStart: (file: File, abortController: AbortController) => void;
  onUploadProgress: (fileName: string, progress: number) => void;
  onUploadCancel: (fileName: string) => void;
}

const FileUploadComponent = ({
  isDisabledUploadingButton,
  onFileUploaded,
  onUploadStart,
  onUploadProgress,
  onUploadCancel,
}: FileUploadComponentProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadFileMutation = useUploadFileMutation();
  const { setError } = useAlert();

  // Валидация файла
  const validateFile = (file: File): boolean => {
    // Проверка размера (20 МБ)
    if (file.size > 20 * 1024 * 1024) {
      setError("Превышен максимальный размер файла (20 Мб)");
      return false;
    }

    // Проверка формата
    if (!['image/png', 'image/svg+xml', 'application/pdf', 'image/jpeg', 'image/jpg',
      'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/illustrator', 'image/vnd.adobe.photoshop', 'application/cdr', 'image/gif',
      'video/mp4', 'video/webm', 'image/heic'].includes(file.type)) {
      setError('Недопустимый формат файла (только png, svg, pdf, jpeg, jpg, pptx, xls, xlsx, ai, psd, cdr, gif, mp4, webm, heic)');
      return false;
    }

    return true;
  };

  // Обработка выбранного файла
  const handleFileSelect = (file: File) => {
    if (validateFile(file)) {
      uploadFile(file);
    }
  };

  const selectImage = () => {
    fileInputRef.current?.click();
  };

  // Загрузка файла
  const uploadFile = (file: File) => {
    const abortController = new AbortController();

    // Уведомляем родителя о начале загрузки
    onUploadStart(file, abortController);

    uploadFileMutation.mutate(
      {
        file,
        abortController,
        onProgress: (progress: number) => {
          onUploadProgress(file.name, progress);
        }
      },
      {
        onSuccess: (data) => {
          // const fileUploadModel: FileUploadModel = {
          //   file: file,
          //   id: data.id
          // };
          const uploadedFile: UploadingFile = {
            file: file,
            remoteFile: data,
            progress: 100.0,
            abortController: abortController
          }
          onFileUploaded(uploadedFile);
        },
        onError: (error) => {
          if (error.name !== 'AbortError') {
            // Ошибка не из-за отмены загрузки
          }
          onUploadCancel(file.name);
        },
      }
    );
  };

  return (
    <Stack gap="normal">
      <input
        ref={fileInputRef}
        type="file"
        accept=".png,.svg,.pdf,.jpeg,.jpg,.pptx,.xls,.xlsx,.ai,.psd,.cdr,.gif,.mp4,.webm,.heic"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelect(file);
          e.target.value = "";
        }}
        className="hidden"
      />
      <Stack direction="horizontal" gap="normal" justify="start">
        <Button
          variant="default"
          leadingVisual={PlusIcon}
          onClick={selectImage}
          disabled={isDisabledUploadingButton}
        >
          Загрузить фото или видео
        </Button>
        <div></div>
      </Stack>
    </Stack>
  );
};

export default FileUploadComponent;